import React, { useState } from "react"
import addToMailchimp from "gatsby-plugin-mailchimp"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Input, Icon } from "@mostlycode/ui"
import Button from "../Button"
import getTranslationsByLocale from "./helper"

const StyledWrapper = styled.div`
  min-height: 12rem;
  margin-top: 2rem;
  color: ${(props) => props.theme.colors.kelp};
  max-width: 80rem;

  ${(props) => props.theme.media.tablet_landscape_up`
    margin: 4rem auto;
  `}
`

const StyledForm = styled.form`
  display: grid;
  grid-template-rows: 1fr;
  grid-row-gap: 1rem;
`

const StyledInput = styled(Input)`
  max-width: none;
  line-height: 1;
  transition: all ease-out 0.1s;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0);
  border-radius: 5px;
  margin-right: 1rem;
  &:focus {
    outline: none;
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
  }
`

const StyledResponse = styled.p``

const StyledField = styled.div`
  ${(props) => props.theme.media.tablet_landscape_up`
    display: grid;
    grid-column-gap: 1rem;
    grid-template-columns: 1.5fr 1.5fr 1fr;
  `}
`

const StyledValidationMessage = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.colors.poppy};
  ${(props) => props.theme.media.tablet_landscape_up`
    margin: 0 auto;
    transform: translateX(1.25rem);
  `}
`

const StyledCheckbox = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;

    &:checked + span {
      background: ${(props) => props.theme.colors.kelp};

      svg {
        opacity: 1;
        fill: ${(props) => props.theme.colors.plush};
      }
    }
  }

  span {
    margin-right: 1.5rem;
    border-radius: 5px;
    width: 3rem;
    height: 3rem;
    border: 1px solid rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    position: relative;
    background: transparent;
    transition: all ease-out 0.15s;

    svg {
      width: 1.6rem;
      height: 1.6rem;
      transition: all ease-out 0.15s;
      opacity: 0;
    }
  }

  label {
    pointer-events: none;
    align-self: center;
  }

  a {
  }

  ${(props) => props.theme.media.tablet_landscape_up`
    margin: 0 auto;
  `}
`

const StyledButton = styled(Button)`
  ${(props) => props.theme.media.tablet_landscape_up`
    justify-self: start;
  `}
`

function NewsletterSignup({
  legal,
  input_placeholder,
  submit_button_text,
  success_message,
  error_message,
  retry_link_text,
  group_id,
  group_option,
  ...props
}) {
  const defaultTranslation = getTranslationsByLocale(props.pageContext.lang)

  const [result, setResult] = useState(null)
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [checked, setChecked] = useState(false)
  const [invalid, setInvalid] = useState(false)

  const fields = {}

  // 2. via `async/await`
  async function doSubmit() {
    fields[`group[${group_id}][${group_option}]`] = group_option
    const response = await addToMailchimp(email, {
      FNAME: firstName,
      ...fields,
    })
    // I recommend setting `result` to React state
    // but you can do whatever you want
    setResult(response)
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!checked) {
      setInvalid(true)
      return
    }

    doSubmit()
  }

  function handleCheck() {
    setChecked(!checked)
    setInvalid(false)
  }

  return (
    <StyledWrapper>
      {result === null && (
        <StyledForm onSubmit={(e) => handleSubmit(e)} noValidate>
          <StyledField>
            <StyledInput
              type="text"
              placeholder={defaultTranslation.firstname_input_placeholder}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              size="sm"
            />
            <StyledInput
              type="email"
              placeholder={
                input_placeholder || defaultTranslation.input_placeholder
              }
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="sm"
            />
            <StyledButton
              variant="secondary"
              type="submit"
              icon="right"
              invert
              disabled={email === ""}
            >
              <span>
                {submit_button_text || defaultTranslation.submit_button_text}
              </span>{" "}
              <Icon icon="mail" />
            </StyledButton>
          </StyledField>
          <StyledCheckbox onClick={() => handleCheck()}>
            <input
              id="consent"
              type="checkbox"
              checked={checked}
              onChange={(e) => e.preventDefault()}
              required
            />
            <span>
              <Icon icon="check" />
            </span>
            <label htmlFor="consent">
              {legal?.signup_disclaimer ||
                defaultTranslation.legal.signup_disclaimer}
            </label>
          </StyledCheckbox>
          {invalid && (
            <StyledValidationMessage>
              <Icon icon="long-arrow-up" />{" "}
              {legal?.signup_nudge || defaultTranslation.legal.signup_nudge}{" "}
            </StyledValidationMessage>
          )}
        </StyledForm>
      )}
      {result !== null && result.result && result.msg && (
        <>
          <StyledResponse>
            {result.result === "error" ? error_message : success_message}
          </StyledResponse>
          {result.result === "error" && (
            <StyledButton
              size="sm"
              variant="secondary"
              onClick={() => setResult(null)}
            >
              {defaultTranslation.retry_link_text}
            </StyledButton>
          )}
        </>
      )}
    </StyledWrapper>
  )
}

export default NewsletterSignup

NewsletterSignup.defaultProps = {
  group_id: "69468",
  group_option: "1",
}

NewsletterSignup.propTypes = {
  group_id: PropTypes.string,
  group_option: PropTypes.string,
}
